import { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { LOGIN_GET_USER_PROFILE_API } from "../../services/apiConstants";
import { downloadCertificate, viewCertificate } from "../../utils/certificateGenerator";
import "./Dashboard.css";

function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedDesc, setSelectedDesc] = useState("");
  const [showCertificate, setShowCertificate] = useState(false);
  const [certificateUrl, setCertificateUrl] = useState("");

  const enrolledExams = user?.mockExams ?? [];
  const mockExamMaster = user?.mockExamMaster ?? [];
  const trialNotice = location.state?.trialCompleted ? location.state : null;
  const hasTakenScore = (value) => value !== -1 && value !== null && value !== undefined;
  const hasCompletedTrial = (exam) => hasTakenScore(exam.trialscore);
  const hasCompletedAttempt1 = (exam) => hasTakenScore(exam.attempt1score);
  const hasCompletedAttempt2 = (exam) => hasTakenScore(exam.attempt2score);
  const toScore = (value) => {
    const score = Number(value);
    return Number.isFinite(score) && score >= 0 ? score : 0;
  };
  const getAverageScore = (exam) => {
    const attempts = [exam.attempt1score, exam.attempt2score]
      .filter(hasTakenScore)
      .map(Number)
      .filter(Number.isFinite);

    if (attempts.length === 0) return 0;
    return Math.round(attempts.reduce((total, value) => total + value, 0) / attempts.length);
  };
  const getGradeFromPercentage = (score) => {
    if (score >= 90) return "A+";
    if (score >= 80) return "A";
    if (score >= 70) return "B";
    if (score >= 60) return "C";
    if (score > 50) return "D";
    return "Needs Improvement";
  };
  const getGradeDisplay = (exam) => {
    if (!hasCompletedAttempt1(exam)) return "-";

    const score = hasCompletedAttempt2(exam) ? getAverageScore(exam) : toScore(exam.attempt1score);
    return getGradeFromPercentage(score);
  };
  const hasPaidForExam = (exam) => {
    const paidExamIds = JSON.parse(localStorage.getItem("paidExamIds") || "[]");
    return exam.paid === true || exam.isPaid === true || paidExamIds.includes(exam.mockExamId);
  };
  const canGenerateCertificate = (exam) =>
    hasCompletedAttempt1(exam) && hasCompletedAttempt2(exam) && getAverageScore(exam) > 50;

  const scoreChartData = enrolledExams.map((exam) => ({
    name: exam.mockExamDesc || exam.mockExamId,
    Trial: toScore(exam.trialscore),
    Attempt1: toScore(exam.attempt1score),
    Attempt2: toScore(exam.attempt2score),
    Average: getAverageScore(exam),
  }));
  const completedExamCount = enrolledExams.filter(
    (exam) => hasCompletedAttempt1(exam) || hasCompletedAttempt2(exam)
  ).length;
  const trialOnlyCount = enrolledExams.filter(
    (exam) => hasCompletedTrial(exam) && !hasCompletedAttempt1(exam) && !hasCompletedAttempt2(exam)
  ).length;

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token || token === "null" || token === "undefined" || token.trim() === "") {
      navigate("/login", { replace: true });
      return;
    }

    axios
      .get(LOGIN_GET_USER_PROFILE_API, {
        headers: { Authorization: `${token}`, "Content-Type": "application/json" },
      })
      .then((res) => {
        setUser(res.data);
        localStorage.setItem(
          "userProfile",
          JSON.stringify({
            firstName: res.data.firstName,
            lastName: res.data.lastName,
            email: res.data.email,
          })
        );
      })
      .catch((err) => {
        console.error(err);
        navigate("/login");
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const handleStartTrial = (mockExamId, price, mockExamDesc) => {
    const examData = { mockExamId, price, mockExamDesc };
    localStorage.setItem("examData", JSON.stringify(examData));
    localStorage.removeItem("fullExamAnswers");
    localStorage.removeItem("fullExamScore");
    navigate("/trialPage", { state: examData });
  };

  const handleFullExamAction = (exam) => {
    const paymentData = {
      mockExamId: exam.mockExamId,
      price: exam.price,
      mockExamDesc: exam.mockExamDesc,
    };

    localStorage.setItem("paymentData", JSON.stringify(paymentData));
    localStorage.removeItem("fullExamAnswers");
    localStorage.removeItem("fullExamScore");

    if (!hasPaidForExam(exam)) {
      navigate("/UPIPayment", { state: paymentData });
      return;
    }

    navigate("/fullexamdisplay", { state: { ...paymentData, paid: true } });
  };

  const handleCertificate = (mode, exam) => {
    const averageScore = getAverageScore(exam);
    if (!canGenerateCertificate(exam)) {
      alert("Certificate is available after both attempts are completed with more than 50% average score.");
      return;
    }

    if (mode === "download") {
      downloadCertificate(user, exam.mockExamDesc, averageScore);
      return;
    }

    viewCertificate(user, exam.mockExamDesc, averageScore, setCertificateUrl, setShowCertificate);
  };

  if (loading) return <p className="dashboard-loading">Loading Dashboard...</p>;

  return (
    <div className="dashboard-container">
      <main className="dashboard-main">
        <section className="dashboard-welcome">
          <div className="dashboard-welcome-inline">
            <h2>Welcome, {user.firstName} {user.lastName || ""}</h2>
            <p>{user.email}</p>
          </div>
        </section>

        {trialNotice && (
          <section className="dashboard-unlock-notice">
            <span><strong>Trial completed.</strong> Pay and unlock the full exam to continue with Attempt 1 and Attempt 2.</span>
            <button
              className="btn"
              onClick={() =>
                navigate("/UPIPayment", {
                  state: {
                    mockExamId: trialNotice.mockExamId,
                    mockExamDesc: trialNotice.mockExamDesc,
                    price: trialNotice.price,
                  },
                })
              }
            >
              Pay and Unlock Full Exam
            </button>
          </section>
        )}

        <div className="dashboard-overview-row">
          <section className="dashboard-visuals">
            <div className="metric-grid">
              <span><strong>{enrolledExams.length}</strong> Enrolled</span>
              <span><strong>{completedExamCount}</strong> Full</span>
              <span><strong>{trialOnlyCount}</strong> Trial</span>
              <span><strong>{mockExamMaster.length}</strong> Available</span>
            </div>

            <div className="chart-box">
              <h4>Scores</h4>
              {scoreChartData.length === 0 ? (
                <p className="chart-empty">No score data yet.</p>
              ) : (
                <ResponsiveContainer width="100%" height={210}>
                  <BarChart
                    data={scoreChartData}
                    margin={{ top: 20, right: 6, left: -24, bottom: 0 }}
                    barCategoryGap="28%"
                    barGap={3}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} height={36} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="Trial" fill="#2563eb" radius={[4, 4, 0, 0]}>
                      <LabelList dataKey="Trial" position="top" fontSize={10} />
                    </Bar>
                    <Bar dataKey="Attempt1" fill="#16a34a" radius={[4, 4, 0, 0]}>
                      <LabelList dataKey="Attempt1" position="top" fontSize={10} />
                    </Bar>
                    <Bar dataKey="Attempt2" fill="#f59e0b" radius={[4, 4, 0, 0]}>
                      <LabelList dataKey="Attempt2" position="top" fontSize={10} />
                    </Bar>
                    <Bar dataKey="Average" fill="#64748b" radius={[4, 4, 0, 0]}>
                      <LabelList dataKey="Average" position="top" fontSize={10} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </section>

          <section className="dashboard-exams dashboard-progress-panel">
            <h3>Progress Snapshot</h3>
            {enrolledExams.length === 0 ? (
              <p>No exams enrolled yet.</p>
            ) : (
              <div className="table-scroll">
                <table className="exam-table compact-table">
                  <thead>
                    <tr>
                      <th>Exam</th>
                      <th>Attempt 1</th>
                      <th>Attempt 2</th>
                      <th>Grade</th>
                      <th>Certificate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enrolledExams.map((exam) => (
                      <tr key={exam.mockExamId}>
                        <td>
                          <button
                            className="link-button"
                            onClick={() => {
                              setSelectedDesc(exam.mockExamDesc);
                              setShowPopup(true);
                            }}
                          >
                            {exam.mockExamDesc}
                          </button>
                        </td>
                        <td>
                          {hasCompletedAttempt1(exam) ? (
                            exam.attempt1score
                          ) : (
                            <button
                              className="btn"
                              disabled={!hasCompletedTrial(exam)}
                              title={!hasCompletedTrial(exam) ? "Complete Trial first" : "Take Attempt 1"}
                              onClick={() => handleFullExamAction(exam)}
                            >
                              Pay Rs {exam.price}
                            </button>
                          )}
                        </td>
                        <td>
                          {hasCompletedAttempt2(exam) ? (
                            exam.attempt2score
                          ) : (
                            <button
                              className="btn"
                              disabled={!hasCompletedAttempt1(exam)}
                              title={!hasCompletedAttempt1(exam) ? "Complete Attempt 1 first" : "Take Attempt 2"}
                              onClick={() => handleFullExamAction(exam)}
                            >
                              Attempt 2
                            </button>
                          )}
                        </td>
                        <td>{getGradeDisplay(exam)}</td>
                        <td>
                          <button
                            className="btn"
                            disabled={!canGenerateCertificate(exam)}
                            title={!canGenerateCertificate(exam) ? "Requires both attempts and more than 50% average" : "View certificate"}
                            onClick={() => handleCertificate("view", exam)}
                          >
                            View
                          </button>
                          <button
                            className="btn"
                            disabled={!canGenerateCertificate(exam)}
                            title={!canGenerateCertificate(exam) ? "Requires both attempts and more than 50% average" : "Download certificate"}
                            onClick={() => handleCertificate("download", exam)}
                          >
                            Download
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>

        <section className="dashboard-exams">
          <h3>Test Pathways for Your Role</h3>
          {mockExamMaster.length === 0 ? (
            <p>No exams available.</p>
          ) : (
            <div className="table-scroll">
              <table className="exam-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Level</th>
                    <th>Duration</th>
                    <th>Price</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {mockExamMaster.map((exam) => (
                    <tr key={exam.mockExamId}>
                      <td>{exam.title}</td>
                      <td>{exam.level}</td>
                      <td>{exam.durationMinutes} mins</td>
                      <td>Rs {exam.price}</td>
                      <td>
                        <button
                          className="btn btn-trial"
                          onClick={() => handleStartTrial(exam.mockExamId, exam.price, exam.title)}
                        >
                          Start Trial
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>

      {showPopup && (
        <div className="popup-overlay">
          <div className="popup-box">
            <h3>Exam Description</h3>
            <p>{selectedDesc}</p>
            <button className="btn" onClick={() => setShowPopup(false)}>Close</button>
          </div>
        </div>
      )}

      {showCertificate && (
        <div className="popup-overlay">
          <div className="certificate-popup">
            <h3>Certificate Preview</h3>
            <iframe src={certificateUrl} width="100%" height="500px" title="Certificate" />
            <button className="btn" onClick={() => setShowCertificate(false)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
